import { useState, useEffect } from 'react';
import { Table, Button, Input } from 'animal-island-ui';
import type { TableColumn } from 'animal-island-ui';
import { useAppStore } from '../store/useAppStore';
import clsx from 'clsx';

export default function LedgerPanel({ eventId }: { eventId: number }) {
  const { ledger, addLedgerEntry, loadLedger } = useAppStore();
  const list = ledger[eventId] || [];
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({ type: 'expense' as 'expense' | 'income', category: '', amount: 0, note: '' });
  const [warning, setWarning] = useState('');

  useEffect(() => {
    loadLedger(eventId);
  }, [eventId, loadLedger]);

  const budgets = list.filter((e) => e.type === 'budget');
  const expenses = list.filter((e) => e.type === 'expense');
  const incomes = list.filter((e) => e.type === 'income');
  const totalBudget = budgets.reduce((s, e) => s + e.amount, 0);
  const totalExpense = expenses.reduce((s, e) => s + e.amount, 0);
  const totalIncome = incomes.reduce((s, e) => s + e.amount, 0);
  const balance = totalBudget - totalExpense;
  const usageRate = totalBudget > 0 ? (totalExpense / totalBudget) * 100 : 0;

  // Table 数据
  const tableData: Record<string, unknown>[] = list.map((e) => ({
    key: e.id,
    type: e.type === 'budget' ? '预算' : e.type === 'expense' ? '支出' : '收入',
    category: e.category,
    amount: `¥${e.amount.toLocaleString()}`,
    note: e.note || '-',
    date: e.date,
  }));

  const columns: TableColumn[] = [
    { title: '类型', dataIndex: 'type', width: 70, align: 'center' },
    { title: '类别', dataIndex: 'category', width: 100 },
    { title: '金额', dataIndex: 'amount', width: 100, align: 'right' },
    { title: '备注', dataIndex: 'note' },
    { title: '日期', dataIndex: 'date', width: 110 },
  ];

  const categoryStats: Record<string, { budget: number; expense: number }> = {};
  budgets.forEach((b) => {
    if (!categoryStats[b.category]) categoryStats[b.category] = { budget: 0, expense: 0 };
    categoryStats[b.category].budget += b.amount;
  });
  expenses.forEach((e) => {
    if (!categoryStats[e.category]) categoryStats[e.category] = { budget: 0, expense: 0 };
    categoryStats[e.category].expense += e.amount;
  });

  const handleAdd = async () => {
    if (!newEntry.category || newEntry.amount <= 0) return;
    const cat = categoryStats[newEntry.category];
    await addLedgerEntry(eventId, {
      type: newEntry.type,
      category: newEntry.category,
      amount: newEntry.amount,
      note: newEntry.note,
    });
    if (cat && newEntry.type === 'expense' && (cat.expense + newEntry.amount) > cat.budget * 1.1) {
      setWarning('该项支出已超出预算10%！开源节流啊！💰');
      setTimeout(() => setWarning(''), 3000);
    }
    setNewEntry({ type: 'expense', category: '', amount: 0, note: '' });
    setShowAddForm(false);
  };

  return (
    <div id="panel-ledger" className="ac-card p-5 relative">
      <div id="ledger-header" className="flex items-center gap-2 mb-4">
        <span className="text-2xl">💰</span>
        <h3 className="font-bold text-lg text-gray-800">喜事账本</h3>
        <span className="text-xs text-gray-400 ml-auto">财神管家</span>
      </div>

      {warning && (
        <div className="mb-3 px-4 py-2 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600 flex items-center gap-2">
          <span>🧧</span>
          <span className="font-medium">财神警告！</span>
          <span>{warning}</span>
        </div>
      )}

      {/* 总览卡片 */}
      <div id="ledger-summary" className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
          <p className="text-xs text-blue-400">总预算</p>
          <p className="font-bold text-lg text-blue-600">¥{totalBudget.toLocaleString()}</p>
        </div>
        <div className="p-3 rounded-xl bg-red-50 border border-red-100">
          <p className="text-xs text-red-400">已支出</p>
          <p className="font-bold text-lg text-red-600">¥{totalExpense.toLocaleString()}</p>
        </div>
        <div className="p-3 rounded-xl bg-green-50 border border-green-100">
          <p className="text-xs text-green-400">礼金收入</p>
          <p className="font-bold text-lg text-green-600">¥{totalIncome.toLocaleString()}</p>
        </div>
      </div>

      {/* 预算进度 */}
      <div id="ledger-progress" className="mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>预算使用率</span>
          <span className={clsx(usageRate > 90 && 'text-red-500 font-bold')}>
            {usageRate.toFixed(1)}% {usageRate > 90 && '⚠️'}
          </span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${Math.min(usageRate, 100)}%`,
              background: usageRate > 90 ? '#e63946' : usageRate > 70 ? '#f4a261' : '#52b788',
            }}
          />
        </div>
        <p className={clsx('text-xs mt-1', balance >= 0 ? 'text-green-500' : 'text-red-500')}>
          {balance >= 0 ? `剩余预算 ¥${balance.toLocaleString()}` : `超支 ¥${Math.abs(balance).toLocaleString()}`}
        </p>
      </div>

      {/* 分类明细 */}
      <div id="ledger-categories" className="space-y-2 mb-4">
        <p className="text-xs font-medium text-gray-500">分类明细</p>
        {Object.entries(categoryStats).map(([cat, stats]) => {
          const rate = stats.budget > 0 ? (stats.expense / stats.budget) * 100 : 0;
          const overBudget = rate > 110;
          return (
            <div key={cat} className="flex items-center gap-3 p-2 rounded-lg bg-amber-50/50">
              <span className="text-sm font-medium text-gray-600 w-20">{cat}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(rate, 100)}%`,
                    background: overBudget ? '#e63946' : rate > 80 ? '#f4a261' : '#52b788',
                  }}
                />
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                ¥{stats.expense.toLocaleString()} / ¥{stats.budget.toLocaleString()}
              </span>
              {overBudget && <span className="text-xs">🧧</span>}
            </div>
          );
        })}
      </div>

      {/* 账本表格 */}
      <div id="ledger-table" className="mb-4">
        <p className="text-xs font-medium text-gray-500 mb-2">收支明细</p>
        <Table columns={columns} dataSource={tableData} striped />
      </div>

      {/* 添加记录 */}
      {showAddForm ? (
        <div id="ledger-add-form" className="p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
          <div className="flex gap-2">
            <select
              value={newEntry.type}
              onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value as 'expense' | 'income' })}
              className="text-sm px-2 py-1.5 rounded-lg border border-amber-200 bg-white"
            >
              <option value="expense">支出</option>
              <option value="income">收入</option>
            </select>
            <Input
              size="small"
              placeholder="类别"
              value={newEntry.category}
              onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}
              className="flex-1"
            />
            <input
              type="number"
              placeholder="金额"
              value={newEntry.amount || ''}
              onChange={(e) => setNewEntry({ ...newEntry, amount: Number(e.target.value) })}
              className="w-24 text-sm px-2 py-1.5 rounded-lg border border-amber-200 bg-white"
            />
          </div>
          <div className="flex gap-2">
            <Input
              size="small"
              placeholder="备注"
              value={newEntry.note}
              onChange={(e) => setNewEntry({ ...newEntry, note: e.target.value })}
              className="flex-1"
            />
            <Button type="primary" onClick={handleAdd}>保存</Button>
            <Button onClick={() => setShowAddForm(false)}>取消</Button>
          </div>
        </div>
      ) : (
        <Button
          id="ledger-add-btn"
          block
          type="dashed"
          onClick={() => setShowAddForm(true)}
        >
          + 记一笔
        </Button>
      )}
    </div>
  );
}
